import { useEffect, useRef, useState } from "react";

/** Baseline reveal speed, in characters/second, once the display has caught up to the stream. */
export const BASE_CHARS_PER_SECOND = 40;

/** Ceiling on reveal speed, so even a large backlog still animates instead of jumping. */
const MAX_CHARS_PER_SECOND = 160;

/** Extra chars/sec per char of backlog, before the ceiling above applies. */
const CATCHUP_ACCELERATION_PER_SECOND = 2;

/**
 * Reveals `targetText` at a bounded rate instead of jumping straight to whatever
 * arrived on the last render, so an uneven stream of SSE deltas reads as a smooth
 * typewriter rather than bursty chunks. Pure history (`isLive` false from the very
 * first render) shows instantly. Once a message has streamed at all, the reveal keeps
 * draining any backlog even after the stream ends — it never hard-flushes to the end,
 * so it doesn't visibly "jump" on completion.
 */
export const useTypewriterReveal = (targetText: string, isLive: boolean): string => {
  const [revealedLength, setRevealedLength] = useState(() => (isLive ? 0 : targetText.length));
  const targetRef = useRef(targetText);
  const isLiveRef = useRef(isLive);
  const everLiveRef = useRef(isLive);
  const revealedFloatRef = useRef(revealedLength);

  targetRef.current = targetText;
  isLiveRef.current = isLive;
  if (isLive) everLiveRef.current = true;

  useEffect(() => {
    // Pure history (never streamed): reveal everything immediately.
    if (!everLiveRef.current) {
      revealedFloatRef.current = targetText.length;
      setRevealedLength(targetText.length);
      return undefined;
    }

    let frame = 0;
    let lastTimestamp: number | null = null;

    const tick = (timestamp: number) => {
      const dtSeconds = (timestamp - (lastTimestamp ?? timestamp)) / 1000;
      lastTimestamp = timestamp;

      const { length } = targetRef.current;
      const backlog = length - revealedFloatRef.current;
      if (backlog > 0) {
        const rate = Math.min(
          MAX_CHARS_PER_SECOND,
          BASE_CHARS_PER_SECOND + backlog * CATCHUP_ACCELERATION_PER_SECOND
        );
        revealedFloatRef.current = Math.min(length, revealedFloatRef.current + rate * dtSeconds);
        setRevealedLength((prev) => Math.max(prev, Math.floor(revealedFloatRef.current)));
      }

      if (revealedFloatRef.current < targetRef.current.length || isLiveRef.current) {
        frame = requestAnimationFrame(tick);
      }
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
    // targetText is read via targetRef inside the loop so new deltas don't restart it;
    // isLive flipping false re-enters this effect but keeps draining any backlog instead
    // of resetting it, so the tail end still animates rather than jumping.
  }, [isLive]);

  return targetText.slice(0, revealedLength);
};
